import type { AnalysisSection } from "../../music/analyzer-types";
import { LocalTransport } from "../../track/local-transport";

export type SectionTipsEvent =
  | { type: "show"; section: AnalysisSection; sectionIndex: number; totalSections: number }
  | { type: "dismiss" };

export type SectionTipsListener = (event: SectionTipsEvent) => void;

/**
 * Detects section boundaries during playback and pauses the transport
 * to display a tip/explain interstitial before continuing.
 *
 * Follows the same event-driven pattern as CountInComponent.
 */
export class SectionTipsComponent {
  private listeners = new Set<SectionTipsListener>();
  private sections: AnalysisSection[] = [];
  private _enabled = true;
  private _showing = false;
  private currentSectionIndex = -1;
  private resolveShow: ((completed: boolean) => void) | null = null;

  private boundOnSchedule: (fromTick: number, scheduleUntilTick: number) => void;
  private unregisterSchedule: (() => void) | null = null;
  private unregisterEvents: (() => void) | null = null;

  constructor(private readonly localTransport: LocalTransport) {
    this.boundOnSchedule = (fromTick, scheduleUntilTick) =>
      this.onSchedule(fromTick, scheduleUntilTick);

    // Register on the transport's tick scheduler to detect section boundaries
    this.unregisterSchedule = this.localTransport.register(this.boundOnSchedule);

    // Listen to seek events to update currentSectionIndex without triggering
    this.unregisterEvents = this.localTransport.on((event) => {
      if (event.type === "seek") {
        this.syncSectionIndex(event.tick);
      }
    });
  }

  /** Set the sections to monitor. Resets tracking state. */
  setSections(sections: AnalysisSection[]): void {
    this.sections = sections;
    this.currentSectionIndex = -1;
    this.syncSectionIndex(this.localTransport.currentTick());
  }

  /** Enable or disable section tips. */
  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
    if (!enabled && this._showing) {
      this.dismiss();
    }
  }

  get enabled(): boolean {
    return this._enabled;
  }

  get showing(): boolean {
    return this._showing;
  }

  /**
   * Show the tip for the section at `tick`, if one exists.
   * Returns a Promise that resolves `true` when dismissed, or `false` if
   * cancelled/no tip. Does NOT auto-resume playback, the caller decides.
   *
   * Used by Studio.startWithCountIn() to chain: Tip → Count-in → Play.
   */
  showAtTick(tick: number): Promise<boolean> {
    if (!this._enabled || this.sections.length === 0) {
      return Promise.resolve(false);
    }

    // Find the section that contains this tick
    let index = -1;
    for (let i = this.sections.length - 1; i >= 0; i--) {
      if (tick >= this.sections[i].startTick) {
        index = i;
        break;
      }
    }

    if (index < 0 || !this.hasTipContent(this.sections[index])) {
      return Promise.resolve(false);
    }

    this.currentSectionIndex = index;
    this.show(this.sections[index], index);

    return new Promise<boolean>((resolve) => {
      this.resolveShow = resolve;
    });
  }

  /**
   * Called by the presentational component when the interstitial completes
   * (auto-hold timeout or user skip).
   *
   * If triggered during playback (onSchedule), resumes playback.
   * If triggered via showAtTick(), resolves the promise (caller handles resume).
   */
  complete(): void {
    if (!this._showing) {
return;
}

    const resolve = this.resolveShow;
    this.resolveShow = null;
    this.dismiss();

    if (resolve) {
      resolve(true);
    } else {
      // Mid-playback trigger, auto-resume
      this.localTransport.play();
    }
  }

  /** Cancel the interstitial without resuming playback. */
  cancel(): void {
    if (!this._showing) {
return;
}

    this.dismiss();
  }

  /** Subscribe to section tips events. Returns an unsubscribe function. */
  on(callback: SectionTipsListener): () => void {
    this.listeners.add(callback);

    return () => {
      this.listeners.delete(callback);
    };
  }

  dispose(): void {
    if (this._showing) {
      this.dismiss();
    }

    this.unregisterSchedule?.();
    this.unregisterEvents?.();
    this.listeners.clear();
  }

  /**
   * Called every ~25ms with the tick window being scheduled.
   * Checks if any section boundary falls within the window.
   */
  private onSchedule(fromTick: number, scheduleUntilTick: number): void {
    if (!this._enabled || this._showing || this.sections.length === 0) {
return;
}

    for (let i = 0; i < this.sections.length; i++) {
      const section = this.sections[i];

      // Check if section start falls within the current scheduling window
      if (section.startTick >= fromTick && section.startTick < scheduleUntilTick) {
        // Only trigger if we're entering a new section
        if (i !== this.currentSectionIndex && this.hasTipContent(section)) {
          this.currentSectionIndex = i;
          this.show(section, i);

          return;
        }

        // Update index even if no tip content
        this.currentSectionIndex = i;
      }
    }
  }

  /** Update currentSectionIndex to match the given tick (e.g. after a seek). */
  private syncSectionIndex(tick: number): void {
    if (this.sections.length === 0) {
      this.currentSectionIndex = -1;

      return;
    }

    // Find which section contains the tick
    for (let i = this.sections.length - 1; i >= 0; i--) {
      if (tick >= this.sections[i].startTick) {
        this.currentSectionIndex = i;

        return;
      }
    }

    this.currentSectionIndex = -1;
  }

  private hasTipContent(section: AnalysisSection): boolean {
    return !!(section.explain || section.tip);
  }

  private show(section: AnalysisSection, index: number): void {
    this._showing = true;
    this.localTransport.detach();
    this.localTransport.seekToTick(section.startTick);
    this.emit({
      type: "show",
      section,
      sectionIndex: index,
      totalSections: this.sections.length,
    });
  }

  private dismiss(): void {
    this._showing = false;

    // Resolve any pending showAtTick() promise as cancelled
    if (this.resolveShow) {
      this.resolveShow(false);
      this.resolveShow = null;
    }

    this.emit({ type: "dismiss" });
  }

  private emit(event: SectionTipsEvent): void {
    this.listeners.forEach((cb) => cb(event));
  }
}
